/**
 * Weave design tokens, and the named aliases the app's theme needs.
 *
 * Weave's legacy theme does not expose its tokens on the MUI theme, so this
 * module re-exports the same JSON that Weave core imports
 * (`@weave/design-tokens` is already a pinned dependency) and gives the values
 * the app uses role-based names. Theme modules import from here instead of
 * repeating Weave paths.
 *
 * Choices (DEVX-11164, `TOKEN-MAPPING`):
 * - Every alias keeps the value Digital HIG had, so the theme renders exactly
 *   as before. Where Weave's semantic token differs, the generic token with the
 *   DHIG value is used and a comment points at DEVX-11164 for the semantic
 *   alternative. XD has the open questions (error red, success green, disabled
 *   text, secondary text, focus-ring thickness).
 * - Weave's semantic names do not always match the role (the indigo is
 *   `halo-color.default.focus`), so the alias names carry the role.
 * - Values with no Weave token are built from Weave tokens with `alpha()`, or
 *   are local constants (marked below). No colour is written as a literal.
 * - Switching the theme to Weave "brand" means swapping the JSON import here and
 *   re-checking every alias, since brand has different values.
 */
import { alpha } from "@mui/material/styles";
import weaveTokenData from "@weave/design-tokens/dist/json/web-nested.digital-hig-light.json";

export const weave = weaveTokenData;

const { generic, semantic, component } = weave;
const palette = generic.color;
const background = semantic["background-color"];
const textColor = semantic["text-color"];
const halo = semantic["halo-color"].default;

const black = palette.black["100"]["100"];
const white = palette.white["100"];

/**
 * Weave pins `ArtifaktElement` with no fallback (R5). The app keeps the DHIG
 * stack, so a blocked webfont falls back to Arial instead of the browser serif.
 */
export const fontStack = `${generic.font.family}, Arial, sans-serif`;

export const color = {
  transparent: palette.transparent,
  black,
  white,
  /** Primary text and icon ink. */
  ink: textColor.heavy.default,
  /** Body text. */
  inkText: textColor.medium.default,
  /**
   * Secondary text (helper text, placeholders). Weave's semantic alternative is
   * `text-color.light`, which renders lighter. DEVX-11164 (XD question).
   */
  inkMuted: palette.black["60"],
  /**
   * Disabled text. Weave's semantic alternative is `text-color.disabled.default`,
   * which is half as strong. DEVX-11164 (XD question).
   */
  inkDisabled: alpha(black, 0.4),
  inkOnFill: textColor["on-accent"],
  inkVisited: textColor.link.visited,
  /** Indigo focus and accent colour (`halo-color.default.focus`). */
  iris: halo.focus,
  irisHover: halo.hover,
  /**
   * Error ink (`clay` 600). Weave's semantic `status-color.error` is a brighter
   * red. DEVX-11164 (XD question). Also the fill of the error icons and danger
   * buttons, where DHIG used `clay-550` (a hair darker and orange, three RGB
   * steps off); Weave has no token for that step, so the nearest one is used.
   */
  error: palette.clay["600"]["100"],
  /** Error underline and ring (`clay` 500). Same XD question as `error`. */
  errorField: palette.clay["500"]["100"],
  /**
   * Success green (`plant` 500). Weave's semantic `status-color.success` is an
   * olive green. DEVX-11164 (XD question).
   */
  success: palette.plant["500"]["100"],
  /** Warning amber (`gold` 500), for the warning alert border and focus ring. */
  warning: palette.gold["500"]["100"],
  /** Tints of the status colours, behind outlined and standard alerts. */
  irisSlight: palette.iris["500"]["10"],
  successSlight: palette.plant["500"]["10"],
  warningSlight: palette.gold["500"]["10"],
  /**
   * Alert red (`clay` 400, DHIG `fill-alert`). A different red from `error` and
   * `errorField`, as in DHIG. Same XD question as `error`. DEVX-11164.
   */
  alert: palette.clay["400"]["100"],
  alertSlight: palette.clay["400"]["10"],
  /**
   * Link colour of a focused standard `Input` (DHIG `text-link-loud`). Weave has
   * no token for this blue, so it is a local constant (not a literal) until XD
   * picks one. DEVX-11164.
   */
  // DHIG used text-link-loud (rgb 68,68,248) for the standard Input focus ring.
  // Weave has no such token, so use the iris focus colour every other focus
  // ring already uses (a barely visible shift; XD question on DEVX-11164).
  inkLinkLoud: halo.focus,
  surface: background.surface["100"],
  surfaceRaised: background.surface["250"],
  panel: palette.slate["100"]["100"],
  /** Grey ramp steps used by app layouts (borders, hover fills, panels). */
  slate100: palette.slate["100"]["100"],
  slate150: palette.slate["150"]["100"],
  slate200: palette.slate["200"]["100"],
  slate300: palette.slate["300"]["100"],
  slate500: palette.slate["500"]["100"],
  slate550: palette.slate["550"]["100"],
  /** Accent colours for avatars, badges and chart series (generic tokens). */
  accentClay: palette.clay["500"]["100"],
  accentGold: palette.gold["500"]["100"],
  /** Chart series blue (`extended.blue` 500). */
  productBlue: palette.extended.blue["500"]["100"],
  accentPlant: palette.plant["500"]["100"],
  /** Neutral avatar fill (`status-color.neutral.default`). */
  neutral: semantic["status-color"].neutral.default,
  fillPrimary: background.brand,
  fieldFill: background.input.default,
  searchFieldFill: background.input.ghost.default,
  hoverFillSubtle: background.ghost.low.hover,
  pressedFillSubtle: background.ghost.low.pressed,
  activatedFillSubtle: palette.black["05"],
  /** Hover fill on an already activated item (`background-color.ghost.low.pressed`). */
  activatedHoverFillSubtle: background.ghost.low.pressed,
  activatedFillOnFill: palette.white["10"],
  /** Modal backdrop (black at 85 percent). */
  backdrop: palette.black["85"],
  /** Overlays on a dark or accent fill (alert action buttons). */
  hoverFillOnFill: palette.white["12"],
  /** Weave has no 0.16 white, so it is built from the white token. */
  pressedFillOnFill: alpha(white, 0.16),
  pressedFillSubtleStrong: palette.black["15"],
  /** Table row states. */
  rowHover: background.ghost.low.hover,
  rowHoverZebra: background.ghost.high.hover,
  rowPressed: background.ghost.low.pressed,
  rowSelected: background.ghost.low.hover,
  rowSelectedHover: palette.black["100"]["6"],
  /** Weave has no 0.12 black, so it is built from the black token. */
  rowSelectedPressed: alpha(black, 0.12),
  /**
   * Row divider. Weave's semantic `border-color.divider.light` is the same grey
   * on white but a solid colour, so the exact black at 10 percent is used.
   * DEVX-11164.
   */
  rowDivider: palette.black["10"],
  divider: semantic["border-color"].divider.light,
  dividerStrong: semantic["border-color"].divider.heavy,
  /** Faint tint behind a disabled spinner track. Built from the black token. */
  trackFaint: alpha(black, 0.08),
  /** Bar behind tab groups (`background-color.surface.200`). */
  tabBar: background.surface["200"],
  /** Colours for the scroll-button fade. Weave has no zero-alpha white. */
  fadeTransparent: alpha(white, 0),
  fadeSemiTransparent: palette.white["15"],
  fadeSemiOpaque: palette.white["85"],
  fadeOpaque: white,
};

/** `[0, 4, 8, 12, 16, 20, 24, 32, 48, 64]px`, the DHIG scale, named by size. */
export const spacing = {
  none: semantic.spacing.none,
  xxs: semantic.spacing.xxs,
  xs: semantic.spacing.xs,
  s: semantic.spacing.s,
  m: semantic.spacing.m,
  px20: generic.spacing["20"],
  l: semantic.spacing.l,
  xl: semantic.spacing.xl,
  xxl: semantic.spacing.xxl,
  px64: generic.spacing["64"],
};

/** The spacing array behind `theme.spacing(n)`, index 0 to 9. */
export const spacingScale = [
  spacing.none,
  spacing.xxs,
  spacing.xs,
  spacing.s,
  spacing.m,
  spacing.px20,
  spacing.l,
  spacing.xl,
  spacing.xxl,
  spacing.px64,
];

export const radius = {
  none: generic["border-radius"].none,
  small: generic["border-radius"]["2"],
  medium: generic["border-radius"]["4"],
  large: generic["border-radius"]["8"],
};

export const borderWidth = {
  /** Weave has no token for zero. */
  none: "0",
  thin: semantic["border-width"].s,
  thick: generic["border-width"]["2"],
};

export const fontWeight = {
  regular: Number(generic["font-weight"].regular),
  medium: Number(generic["font-weight"].medium),
  bold: Number(generic["font-weight"].bold),
  extraBold: Number(generic["font-weight"]["extra-bold"]),
};

export const fontSize = {
  small: semantic["font-size"].s,
  medium: semantic["font-size"].m,
  large: semantic["font-size"].l,
};

/**
 * DHIG's fluid sizes, keyed by DHIG's `fontSizes` index. Weave's clamp tokens
 * have a different slope (about 1px apart at desktop widths), so these stay
 * local constants until XD accepts Weave's sizes (LESSONS R5, R7).
 */
export const fluidFontSize = {
  4: "clamp(1.125rem, calc(1.06rem + 0.278vw), 1.3125rem)",
  5: "clamp(1.3125rem, calc(1.21rem + 0.463vw), 1.625rem)",
  6: "clamp(1.5rem, calc(1.29rem + 0.926vw), 2.125rem)",
  7: "clamp(1.75rem, calc(1.42rem + 1.48vw), 2.75rem)",
  8: "clamp(2rem, calc(1.50rem + 2.22vw), 3.5rem)",
  9: "clamp(2.3125rem, calc(1.58rem + 3.24vw), 4.5rem)",
  10: "clamp(2.625rem, calc(1.58rem + 4.63vw), 5.75rem)",
};

export const lineHeight = {
  tight: semantic["line-height"].s,
  compact: semantic["line-height"].m,
  default: semantic["line-height"].l,
};

export const letterSpacing = {
  none: generic["letter-spacing"]["0"],
  loose: generic["letter-spacing"]["002"],
};

export const opacity = {
  hidden: generic.opacity["0"],
  disabled: semantic.opacity.disabled,
  visible: generic.opacity["100"],
};

/**
 * Easing curves. `energetic` is Weave's tablist easing. `smooth` has no Weave
 * token, so it stays a local constant.
 */
export const easing = {
  energetic: component.tablist.box["transition-easing"],
  smooth: "cubic-bezier(0.2, 0, 0.8, 1)",
};

/**
 * Modal motion. Weave has no tokens for these curves and the 187.5ms step
 * (`transition-duration.187` is 187ms), so the DHIG values stay local.
 */
export const motion = {
  duration: {
    short: generic["transition-duration"]["125"],
    medium: "187.5ms",
    long: generic["transition-duration"]["250"],
  },
  easing: {
    smooth: easing.smooth,
    slowDown: "cubic-bezier(0, 0, 0.4, 1)",
  },
};

/**
 * DHIG's state transitions. `blur` takes Weave's 250ms. `activate` is 187.5ms in
 * DHIG and 187ms in Weave (`transition-duration.187`); the half millisecond is
 * kept so computed styles stay identical. DEVX-11164.
 */
export const transition = {
  activate: {
    transitionTimingFunction: easing.energetic,
    transitionDuration: "187.5ms",
  },
  blur: {
    transitionTimingFunction: easing.smooth,
    transitionDuration: generic["transition-duration"]["250"],
  },
};

const ring = (width: string, ringColor: string) =>
  `0px 0px 0px ${width} ${ringColor}`;

export const effect = {
  /**
   * Focus ring: indigo over a 1px white layer, as DHIG draws it. Weave's
   * `halo.default.focus` is the indigo layer alone. Ring thickness is an XD
   * question, DEVX-11164.
   */
  focusRingThin: `${ring("2px", color.iris)}, ${ring("1px", color.white)}`,
  /** The thick variant of the focus ring (3px), used on modal close buttons. */
  focusRing: `${ring("3px", color.iris)}, ${ring("1px", color.white)}`,
  /**
   * Modal edge: a hard 3px drop shadow with a black inset outline. Weave has no
   * equivalent (its `shadow.low` is soft), so it is built from the black token.
   */
  elevation: `0px 3px 0px 0px ${black}, 0px 0px 0px 1px ${black} inset`,
  /** Inset 1px dividers, the row divider grey. */
  dividerTop: `0px 1px 0px 0px ${color.rowDivider} inset`,
  dividerBottom: `0px -1px 0px 0px ${color.rowDivider} inset`,
  /** Field resting state: black underline and a 30 percent inset outline. */
  fieldDefault: `0px -1px 0px 0px ${black} inset, ${ring(
    "1px",
    palette.black["30"]
  )} inset`,
  /** Field error state. Weave's own error line is a different red. DEVX-11164. */
  fieldError: `0px -1px 0px 0px ${color.errorField} inset, ${ring(
    "1px",
    color.errorField
  )} inset`,
  /** Bordered table container: inset 50 percent grey ring and a grey underline. */
  container: `${ring(
    "1px",
    alpha(palette.slate["300"]["100"], 0.5)
  )} inset, 0px -1px 0px 0px ${palette.slate["550"]["100"]} inset`,
  /** Dropdown and listbox edge: hard drop shadow with a soft inset outline. */
  dropdown: `0px 2px 0px 0px ${palette.black["60"]}, 0px 1px 0px 0px ${palette.black["30"]} inset, -1px 0px 0px 0px ${palette.black["30"]} inset, 1px 0px 0px 0px ${palette.black["30"]} inset`,
};

export const gradient = {
  /** Sticky list-category title: white that fades out at the bottom. */
  categoryTitle: `linear-gradient(180deg, ${color.white} 0%, ${color.white} 83%, ${color.fadeTransparent} 100%)`,
};
